import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormGroup from "../../../cmp/form/FormGroup";
import Input from "../../../cmp/form/Input";
import Select from "../../../cmp/form/Select";
import TextArea from "../../../cmp/form/TextArea";

const MOCK_CAMPAIGNS: Record<string, typeof EMPTY_FORM> = {
  "1": {
    name: "Spring Sale Announcement",
    audience: "all",
    subject: "Our biggest spring sale is live — up to 40% off!",
    body: "Hi [first_name],\n\nWe are thrilled to announce our biggest spring sale yet! Enjoy up to 40% off sitewide. Share your unique affiliate link and earn commissions on every sale.\n\nHappy promoting,\nThe Team",
    send_mode: "now",
    scheduled_at: "",
  },
  "2": {
    name: "New Product Launch",
    audience: "active",
    subject: "Introducing our brand-new product line",
    body: "Hi [first_name],\n\nWe are excited to share that our new product line just dropped. Be the first to promote it to your audience!\n\nBest,\nThe Team",
    send_mode: "schedule",
    scheduled_at: "2026-04-05T09:00",
  },
  "3": {
    name: "Re-engagement Campaign",
    audience: "inactive",
    subject: "We miss you — here's 20% off to come back",
    body: "Hi [first_name],\n\nWe noticed you have not promoted us recently. We would love to have you back! Use the coupon code WELCOME20 to offer your audience 20% off.\n\nHope to see you soon,\nThe Team",
    send_mode: "now",
    scheduled_at: "",
  },
};

const AUDIENCE_OPTIONS = [
  { label: "All affiliates", value: "all" },
  { label: "Active affiliates", value: "active" },
  { label: "Inactive affiliates", value: "inactive" },
  { label: "Pending approval", value: "pending" },
];

const EMPTY_FORM = {
  name: "",
  audience: "all",
  subject: "",
  body: "",
  send_mode: "now" as "now" | "schedule",
  scheduled_at: "",
};

export default function CrudEmailCampaign() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    setTimeout(() => {
      const mock = MOCK_CAMPAIGNS[id!];
      if (mock) setForm(mock);
      setLoading(false);
    }, 400);
  }, [id, isEdit]);

  const set = useCallback(
    <K extends keyof typeof EMPTY_FORM>(
      key: K,
      value: (typeof EMPTY_FORM)[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const validate = (): string | null => {
    if (!form.name.trim()) return "Campaign name is required.";
    if (!form.subject.trim()) return "Subject line is required.";
    if (!form.body.trim()) return "Email body is required.";
    if (form.send_mode === "schedule" && !form.scheduled_at)
      return "Please pick a date and time to schedule the send.";
    if (
      form.send_mode === "schedule" &&
      form.scheduled_at &&
      new Date(form.scheduled_at) <= new Date()
    )
      return "Scheduled time must be in the future.";
    return null;
  };

  const handleSave = useCallback(
    (sendNow: boolean) => {
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      setSaving(true);
      // Mock save — replace with post/patch API calls when ready
      setTimeout(() => {
        setSaving(false);
        navigate("..", { relative: "path" });
      }, 700);
    },
    [form, id, isEdit, navigate],
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 720 }}>
      <button
        className="btn btn-link ps-0 mb-3 text-decoration-none text-muted"
        onClick={() => navigate("..", { relative: "path" })}
      >
        ← Back to campaigns
      </button>

      <h4 className="mb-1">{isEdit ? "Edit Campaign" : "New Campaign"}</h4>
      <p className="text-muted small mb-4">
        {isEdit
          ? "Update the campaign details below."
          : "Fill in the details below to create a new email campaign."}
      </p>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}

      <div className="card border-0 shadow-sm p-4">
        <FormGroup label="Campaign name" required htmlFor="campaign-name">
          <Input
            id="campaign-name"
            placeholder="e.g. Spring sale announcement"
            value={form.name}
            onChange={(v) => set("name", v)}
          />
        </FormGroup>

        <div className="mt-3">
          <FormGroup label="Audience" required>
            <Select
              options={AUDIENCE_OPTIONS}
              value={form.audience}
              onChange={(v) => set("audience", v)}
            />
          </FormGroup>
        </div>

        <hr className="my-4" />

        <FormGroup label="Subject line" required htmlFor="campaign-subject">
          <Input
            id="campaign-subject"
            placeholder="e.g. Exciting news — our spring sale is live!"
            value={form.subject}
            onChange={(v) => set("subject", v)}
          />
        </FormGroup>

        <div className="mt-3">
          <FormGroup label="Email body" required>
            <TextArea
              rows={10}
              placeholder="Write your email content here..."
              value={form.body}
              onChange={(v) => set("body", v)}
            />
          </FormGroup>
        </div>

        <hr className="my-4" />

        <div className="mt-3">
          <FormGroup label="Send options" required>
            <div className="d-flex gap-3">
              <label
                className="d-flex align-items-center gap-2"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="send_mode"
                  value="now"
                  className="form-check-input mt-0"
                  checked={form.send_mode === "now"}
                  onChange={() => set("send_mode", "now")}
                />
                Send immediately
              </label>
              <label
                className="d-flex align-items-center gap-2"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="send_mode"
                  value="schedule"
                  className="form-check-input mt-0"
                  checked={form.send_mode === "schedule"}
                  onChange={() => set("send_mode", "schedule")}
                />
                Schedule for later
              </label>
            </div>
          </FormGroup>
        </div>

        {form.send_mode === "schedule" && (
          <div className="mt-3">
            <FormGroup label="Send date & time" required htmlFor="scheduled-at">
              <input
                id="scheduled-at"
                type="datetime-local"
                className="form-control"
                value={form.scheduled_at}
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                onChange={(e) => set("scheduled_at", e.target.value)}
              />
            </FormGroup>
          </div>
        )}

        <div className="d-flex gap-2 mt-4">
          {form.send_mode === "schedule" ? (
            <button
              className="btn btn-primary"
              disabled={saving}
              onClick={() => handleSave(false)}
            >
              {saving ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              ) : null}
              Schedule Campaign
            </button>
          ) : (
            <button
              className="btn btn-primary"
              disabled={saving}
              onClick={() => handleSave(true)}
            >
              {saving ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              ) : null}
              Send Now
            </button>
          )}
          <button
            className="btn btn-outline-secondary"
            disabled={saving}
            onClick={() => handleSave(false)}
          >
            Save as Draft
          </button>
          <button
            className="btn btn-link text-muted ms-auto"
            disabled={saving}
            onClick={() => navigate("..", { relative: "path" })}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
